import { datadogLogs } from '@datadog/browser-logs';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';

import { authSlice } from './auth.slice';
import { authService } from './authService';
import { useAppDispatch } from 'src/app/store/hooks';
import { useProfileRetrieveQuery } from 'src/common/external/bambi-api/bambiApi';

export const MAX_MEMBERSHIP_REFETCHES = 2;
export const MEMBERSHIP_REFETCH_DELAY_MS = 1000;

export const MAX_PROFILE_REFETCHES = 2;
export const PROFILE_REFETCH_DELAY_MS = 1000;

type QueryError = FetchBaseQueryError | SerializedError;

// A body fetchBaseQuery cannot parse arrives as PARSING_ERROR with the real
// code on originalStatus, which is where a proxy's HTML 401 hides.
function errorStatus(error: QueryError) {
  if (!('status' in error)) {
    return undefined;
  }
  return error.status === 'PARSING_ERROR' ? error.originalStatus : error.status;
}

// 401/403 is the backend saying this session is no good. A 5xx, a dropped
// connection or a timeout happened on the way there and says nothing about the
// session.
function isSessionRejected(error: QueryError) {
  const status = errorStatus(error);
  return status === 401 || status === 403;
}

export function useFetchAndEnsureProfile() {
  const dispatch = useAppDispatch();
  const {
    data: profile,
    isFetching,
    error,
    refetch,
  } = useProfileRetrieveQuery();
  const [isVerifyingMemberships, setIsVerifyingMemberships] = useState(false);
  const [profileRefetches, setProfileRefetches] = useState(0);
  const [isSessionUnrecoverable, setIsSessionUnrecoverable] = useState(false);

  const isRetryingProfile =
    !!error &&
    !profile &&
    !isSessionRejected(error) &&
    profileRefetches < MAX_PROFILE_REFETCHES;
  // RTK Query reports isLoading for the first attempt only, so the retries need
  // their own term to keep RequireAuth from blanking between spinner and app.
  const isLoadingProfile = !profile && (isFetching || isRetryingProfile);

  useEffect(() => {
    if (profile) {
      dispatch(authSlice.actions.signIn(profile));
    }
  }, [profile, dispatch]);

  useEffect(() => {
    // A failed *refetch* from the membership-verification loop below sets
    // `error` while the loaded `profile` is still present, and that must not
    // bounce an already-authed user. A fetch in flight has not failed yet.
    if (!error || profile || isFetching) {
      return;
    }

    // A trip through Cognito cannot fix a call that never reached the backend,
    // and it costs the whole session, so spend a few attempts first.
    if (isRetryingProfile) {
      const timer = setTimeout(() => {
        setProfileRefetches((attempt) => attempt + 1);
        void refetch();
      }, PROFILE_REFETCH_DELAY_MS);
      return () => clearTimeout(timer);
    }

    datadogLogs.logger.warn('auth.profile.unrecoverable', {
      auth: { status: errorStatus(error) },
    });
    void (async () => {
      // Only an outright false says the browser is staying on this page and
      // something has to be rendered here. Anything else leaves the redirect
      // to land.
      if ((await authService.redirectUnauthedUser()) === false) {
        setIsSessionUnrecoverable(true);
      }
    })();
  }, [error, profile, isFetching, isRetryingProfile, refetch]);

  // A freshly-loaded profile can briefly have no memberships — a stale cached
  // snapshot, or a just-provisioned account whose membership isn't visible yet.
  // Refetch a bounded number of times before trusting the empty result, so
  // RequireAuth doesn't flash the no-organization notice on a transient one.
  useEffect(() => {
    if (!profile || profile.memberships.length > 0) {
      setIsVerifyingMemberships(false);
      return;
    }

    const controller = new AbortController();
    // Read via a call so TS doesn't narrow it to a constant between awaits.
    const isCanceled = () => controller.signal.aborted;
    setIsVerifyingMemberships(true);

    void (async () => {
      for (let attempt = 0; attempt < MAX_MEMBERSHIP_REFETCHES; attempt++) {
        await new Promise((resolve) =>
          setTimeout(resolve, MEMBERSHIP_REFETCH_DELAY_MS)
        );
        if (isCanceled()) return;

        const refreshed = await refetch()
          .unwrap()
          .catch(() => undefined);
        if (isCanceled()) return;
        if (refreshed && refreshed.memberships.length > 0) {
          setIsVerifyingMemberships(false);
          return;
        }
      }
      if (!isCanceled()) setIsVerifyingMemberships(false);
    })();

    return () => {
      controller.abort();
    };
  }, [profile, refetch]);

  return {
    isLoading: isLoadingProfile || isVerifyingMemberships,
    isSessionUnrecoverable,
  };
}
